import { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArchiveIcon } from '@heroicons/react/outline';
import { useAuth } from '../../../contexts/AuthContext';
import {
  NotificationDataFragment,
  useMarkProviderUserNotificationReadMutation,
  useAcceptInvitationMutation,
  useDeclineCollaboratorInvitationMutation,
} from '../../../generated/graphql';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import {
  getNotficationContent,
  NotificationAvatar,
  handleNotificationClick,
  ProviderUserNotificationTypename,
} from '../../lib/notifications';
import { getTimeStamp } from '../../lib/time';
import { logger } from '../../../lib/logger';
import Bolt from '../../svgs/Bolt';

const NotificationItem: FC<{
  notification: NotificationDataFragment;
}> = ({ notification }) => {
  const navigate = useNavigate();

  const { refreshAuthedProviderUserNotifications } = useAuth();

  const [markProviderUserNotificationRead] =
    useMarkProviderUserNotificationReadMutation();
  const [acceptInvitation, { loading: acceptLoading }] =
    useAcceptInvitationMutation();
  const [declineInvitation, { loading: declineLoading }] =
    useDeclineCollaboratorInvitationMutation();

  const onRowClick = () =>
    handleNotificationClick({
      notification,
      markProviderUserNotificationRead,
      navigate,
      refreshAuthedProviderUserNotifications,
      skipMarkRead: false,
    });

  const onButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRowClick();
  };

  const NotificationContent = getNotficationContent(notification);
  let ActionButtons: ReactNode = (
    <Button
      size="small"
      title="View"
      theme="secondary"
      onClick={onButtonClick}
    />
  );

  switch (notification.__typename) {
    case ProviderUserNotificationTypename.ProviderUserNotificationActivation:
      ActionButtons = (
        <Button
          size="small"
          title="Send Intake"
          theme="secondary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/${notification.programId}/follow-ups/new`);
          }}
        />
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationActivityCompleted:
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationAssessmentCompleted:
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationFormNeedsProviderSignature:
      ActionButtons = (
        <Button
          size="small"
          title="Sign form"
          theme="secondary"
          onClick={onButtonClick}
        />
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationComment:
      ActionButtons = (
        <Button
          size="small"
          title="Reply"
          theme="secondary"
          onClick={onButtonClick}
        />
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationInvoicePaid:
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationSessionBooked:
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationPendingProviderMembership:
      if (notification.membershipStatus === 'INVITED') {
        const handleAccept = async (e: React.MouseEvent) => {
          e.stopPropagation();
          try {
            const result = await acceptInvitation({
              variables: {
                input: {
                  membershipId: notification.membershipId,
                },
              },
            });

            if (result.data?.acceptCollaboratorInvitation.success) {
              await refreshAuthedProviderUserNotifications();
            }
          } catch (err) {
            console.error('Failed to accept invitation:', err);
          }
        };

        const handleDecline = async (e: React.MouseEvent) => {
          e.stopPropagation();
          try {
            const result = await declineInvitation({
              variables: {
                input: {
                  membershipId: notification.membershipId,
                },
              },
            });

            if (result.data?.declineCollaboratorInvitation.success) {
              await refreshAuthedProviderUserNotifications();
            }
          } catch (err) {
            console.error('Failed to decline invitation:', err);
          }
        };

        ActionButtons = (
          <div className="flex gap-1">
            <Button
              size="extra-small"
              theme="primary"
              title="Accept"
              onClick={handleAccept}
              disabled={acceptLoading || declineLoading}
            />
            <Button
              size="extra-small"
              theme="alt"
              title="Decline"
              onClick={handleDecline}
              disabled={acceptLoading || declineLoading}
            />
          </div>
        );
      }
      break;
    default:
      break;
  }

  const isStorefrontSignUpNotification =
    notification.__typename ===
    ProviderUserNotificationTypename.ProviderUserNotificationStorefrontSignUp;

  return (
    <div
      onClick={onRowClick}
      className="mb-2 flex w-full cursor-pointer flex-row items-center rounded-lg py-3 px-5 hover:bg-secondary-20 focus:bg-neutral-50 focus:outline-none"
    >
      <NotificationAvatar
        notification={notification}
        size="medium"
        className="mr-4"
      />
      <div className="mr-10 flex-grow">
        <div className="mb-1 text-caption text-green-150 line-clamp-2">
          {NotificationContent}
        </div>
        <div className="text-small-caption text-neutral-110">
          {getTimeStamp(notification.createdAt, true, true)}
        </div>
      </div>
      <div className="flex flex-row">
        <div className="flex w-[3rem] items-center justify-center">
          {isStorefrontSignUpNotification && (
            <Bolt className="h-[1.125rem] w-[1.125rem] text-orange-100" />
          )}
        </div>
        {ActionButtons}
        <IconButton
          className="ml-5"
          iconClassName="w-5 h-5 text-secondary-125"
          IconComponent={ArchiveIcon}
          aria-label="Mark notification read"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await markProviderUserNotificationRead({
                variables: {
                  providerUserNotificationId: notification.id,
                  archivedNotification: true, // Only ArchiveIcon sets this true
                },
              });
              await refreshAuthedProviderUserNotifications();
            } catch (err) {
              logger.error('Failed to archive notification', {
                notificationId: notification.id,
                error: err,
              });
            }
          }}
        />
      </div>
    </div>
  );
};

export default NotificationItem;
